import type { LenderChangeOrigin, ReserveSplitInputs } from '../types/reserveSplitTypes';
import type { OfferType, paymentServicesTypes } from '@makemydeal/dr-platform-types';
import { CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { getSelectedTerm, getOfferDetails } from './offerNodeSelectors';
import type { IStateTreeWithFeatureToggles } from '../types/paymentTypes';

type ProfitabilityData = paymentServicesTypes.ProgramQuoteOutput;

type ProfitabilityStateBase = {
    offer?: {
        offerType?: OfferType | string;
    };
};

type State = ProfitabilityStateBase & IStateTreeWithFeatureToggles;

// TARGET-OFFER
export const getProfitabilityDataByOfferType = (state: State, offerType: OfferType): ProfitabilityData => {
    const selectedTerm: any = offerType === CASH ? getOfferDetails(state, offerType) : getSelectedTerm(state, offerType);

    return {
        pricing: selectedTerm?.pricing,
        profitDetails: selectedTerm?.profitDetails,
        proceeds: selectedTerm?.proceeds
    };
};

// CURRENT-OFFER
export const getCurrentOfferProfitabilityData = (state: State): ProfitabilityData => {
    const offerType = state.offer?.offerType as OfferType;
    return getProfitabilityDataByOfferType(state, offerType);
};

// TOP-LEVEL
export const getPricingData = (state: State) => getCurrentOfferProfitabilityData(state).pricing || {};
export const getProfitDetailsData = (state: State) => getCurrentOfferProfitabilityData(state).profitDetails || {};
export const getProceedsData = (state: State) => getCurrentOfferProfitabilityData(state).proceeds || {};

// PRICING
export const getMsrp = (state: State) => (getPricingData(state) as any).msrp;
export const getInvoice = (state: State) => (getPricingData(state) as any).invoice;
export const getNet = (state: State) => (getPricingData(state) as any).net;
export const getAccessoryTotal = (state: State) => (getPricingData(state) as any).accessoryTotal;
export const getTotalAccessoriesPrice = (state: State) => getPricingData(state).totalAccessoriesPrice || 0;
export const getTotalAccessoriesCost = (state: State) => getPricingData(state).totalAccessoriesCost || 0;
export const getTotalFrontEndProtectionProductsPrice = (state: State) =>
    getPricingData(state).totalFrontEndProtectionProductsPrice || 0;
export const getTotalFrontEndProtectionProductsCost = (state: State) =>
    getPricingData(state).totalFrontEndProtectionProductsCost || 0;
export const getTotalBackEndProtectionProductsPrice = (state: State) =>
    getPricingData(state).totalBackEndProtectionProductsPrice || 0;
export const getTotalBackEndProtectionProductsCost = (state: State) =>
    getPricingData(state).totalBackEndProtectionProductsCost || 0;

// PROFIT
export const getGrossProfit = (state: State) => (getProfitDetailsData(state) as any).grossProfit;
export const getBackEnd = (state: State) => (getProfitDetailsData(state) as any).backEnd;
export const getAccessoryProfit = (state: State) => (getProfitDetailsData(state) as any).accessoryProfit;
export const getReserves = (state: State) => (getProfitDetailsData(state) as any).reserves;
export const getFrontEnd = (state: State) => (getProfitDetailsData(state) as any).frontEnd;
export const getFinanceReserve = (state: State) => (getProfitDetailsData(state) as any).financeReserve;
export const getLeaseReserve = (state: State) => (getProfitDetailsData(state) as any).leaseReserve;
export const getTotalProfit = (state: State) => getProfitDetailsData(state).totalProfit;
export const getAddsToFrontEndCost = (state: State) => getProfitDetailsData(state).addsToFrontEndCost || 0;
export const getAddsToBackEndCost = (state: State) => getProfitDetailsData(state).addsToBackEndCost || 0;
export const getTotalFrontEndAccessoriesProfit = (state: State) => getProfitDetailsData(state).totalFrontEndAccessoriesProfit || 0;
export const getCostReductionFromDealerCash = (state: State) => getProfitDetailsData(state).costReductionFromDealerCash || 0;
export const getTotalFrontEndProtectionProductsProfit = (state: State) =>
    getProfitDetailsData(state).totalFrontEndProtectionProductsProfit || 0;
export const getTotalBackEndAccessoriesProfit = (state: State) => getProfitDetailsData(state).totalBackEndAccessoriesProfit || 0;
export const getTotalBackEndProtectionProductsProfit = (state: State) =>
    getProfitDetailsData(state).totalBackEndProtectionProductsProfit || 0;

// PROCEEDS
export const getDealerCash = (state: State) => (getProceedsData(state) as any).dealerCash;
export const getDealerProfit = (state: State) => (getProceedsData(state) as any).dealerProfit;
export const getCustomerFinancedAmount = (state: State) => (getProceedsData(state) as any).customerFinancedAmount;
export const getCustomerCashDue = (state: State) => (getProceedsData(state) as any).customerCashDue;
export const getManufacturerRebateAmount = (state: State) => (getProceedsData(state) as any).manufacturerRebateAmount;
export const getDealerRebateAmount = (state: State) => (getProceedsData(state) as any).dealerRebateAmount;
export const getFlatFee = (state: State) => getProceedsData(state).flatFee || 0;
export const getReservePercentOfAmountFinanced = (state: State) => getProceedsData(state).reservePercentOfAmountFinanced || 0;

type ReserveFigures = {
    reservePaid?: number;
    totalInterestOnRateMarkup?: number;
    reserveFromInterestParticipation?: number;
};

/**
 * Reserve figures, read from `proceeds` when the term carries them and otherwise from `profit`.
 *
 * Payment Services reports reserve on the term's `profit`, and acceleron maps the saved figures back to
 * the same place (`mapToPayment`). A distinct `proceeds` node only appears on terms that came through
 * the DXG-shaped path, so reading `proceeds` alone leaves the Reserve section empty on an ordinary
 * priced deal. Resolved per key rather than per node: a `proceeds` node that exists but carries none of
 * the reserve keys would otherwise mask figures that are present on `profit`.
 *
 * Deliberately narrower than `getProceedsData`: the three keys below carry the same meaning in both
 * nodes, whereas that accessor also serves `dealerCash`, `customerCashDue` and friends, which do not
 * exist on `profit` at all. Widening it there would change unrelated screens.
 *
 * All three readers break from the `|| 0` convention of their neighbours. The Reserve section has to
 * tell "Payment Services sent nothing" apart from a genuine zero, so it can render a dash and drop the
 * "at N% split" qualifier rather than quote figures that were never returned.
 */
const getReserveData = (state: State): ReserveFigures => {
    const proceeds = getProceedsData(state) as ReserveFigures;
    const offerType = state.offer?.offerType as OfferType;
    const selectedTerm: any = offerType === CASH ? getOfferDetails(state, offerType) : getSelectedTerm(state, offerType);
    const profit: ReserveFigures = selectedTerm?.profit || {};

    return {
        reservePaid: proceeds.reservePaid ?? profit.reservePaid,
        totalInterestOnRateMarkup: proceeds.totalInterestOnRateMarkup ?? profit.totalInterestOnRateMarkup,
        reserveFromInterestParticipation: proceeds.reserveFromInterestParticipation ?? profit.reserveFromInterestParticipation
    };
};

export const getTotalInterestOnRateMarkup = (state: State): number | undefined => getReserveData(state).totalInterestOnRateMarkup;
export const getReservePaid = (state: State): number | undefined => getReserveData(state).reservePaid;
export const getRateAdjustmentRatio = (state: State) => getProceedsData(state).rateAdjustmentRatio || 0;
export const getMarkupPercent = (state: State) => getProceedsData(state).markupPercent || 0;
export const getReserveProfit = (state: State) => getProceedsData(state).reserveProfit || 0;

export const getReserveFromInterestParticipation = (state: State): number | undefined =>
    getReserveData(state).reserveFromInterestParticipation;

export const getReserveSplitOverride = (state: State, offerType?: OfferType): number | undefined =>
    getOfferDetails(state, offerType).reserveSplitOverride;

const getReserveSplitInputs = (state: State, offerType?: OfferType): ReserveSplitInputs =>
    getOfferDetails(state, offerType) as ReserveSplitInputs;

export const getIsReserveSplitClearedByLenderChange = (state: State, offerType?: OfferType): boolean =>
    Boolean(getReserveSplitInputs(state, offerType).isReserveSplitClearedByLenderChange);

export const getReserveSplitClearedFrom = (state: State, offerType?: OfferType): LenderChangeOrigin | undefined =>
    getReserveSplitInputs(state, offerType).reserveSplitClearedFrom;

export const hasIntrinsicOfferProfitability = (state: State): boolean => {
    const profitability = getCurrentOfferProfitabilityData(state);
    return hasProfitabilityData(profitability);
};

export const hasProfitabilityData = (profitability: ProfitabilityData): boolean => {
    return !!(profitability.pricing || profitability.profitDetails || profitability.proceeds);
};

export const hasProceedsData = (state: State): boolean => {
    const offerType = state.offer?.offerType as OfferType;
    const proceeds = getProceedsData(state);
    return (offerType === FINANCE || offerType === LEASE) && Object.keys(proceeds).length > 0;
};

export const getProfitabilityForPayloadByDealType = (state: State, dealType: OfferType): ProfitabilityData => {
    return getProfitabilityDataByOfferType(state, dealType);
};
